// src/components/blog/layouts/BlogHero.tsx

'use client'

import type { BlogPost } from '~/lib/blog/types'
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useTransform
} from 'motion/react'
import { useRef, useMemo, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { cn } from '~/lib/utils'
import { DEFAULT_IMAGE } from '~/lib/blog/config'
import { withDevCacheBust } from '~/lib/utils/url'

/**
 * Props for the BlogHero component
 * @interface BlogHeroProps
 */
export interface BlogHeroProps {
  /** The blog post data to display */
  post: BlogPost
  /** Size variant for the hero section */
  size?: 'small' | 'medium' | 'large'
  /** Amount of blur to apply on scroll (in pixels) */
  blurAmount?: number
  /** Opacity of the overlay gradient (0-1) */
  overlayOpacity?: number
  /** Optional override for the post's hero image URL */
  imageUrl?: string
  /** Whether to show the author information */
  showAuthor?: boolean
  /** Whether to show the topic link */
  showTopic?: boolean
  /** Whether to show tags */
  showTags?: boolean
  /** Whether to show reading time */
  showReadingTime?: boolean
  /** Whether to show publication date */
  showDate?: boolean
  /** Whether to show the description and content overlay */
  showDescription?: boolean
  /** Whether to show the hero image */
  showImage?: boolean
  /** Additional CSS classes for the image */
  imageClassName?: string
  /** Additional CSS classes for the content container */
  contentClassName?: string
  /** Whether to prioritize image loading */
  priority?: boolean
}

/**
 * BlogHero component displays a parallax hero section for blog posts
 * with scroll-based animations and visual effects.
 * 
 * Features:
 * - Parallax scrolling effect on background image
 * - Progressive blur on scroll
 * - Dynamic overlay opacity
 * - Respects user's reduced motion preferences
 * - Optimized performance using useMotionTemplate
 * 
 * @param {BlogHeroProps} props - Component props
 * @returns {JSX.Element} The rendered hero section
 */
export function BlogHero({
  post,
  size = 'large',
  blurAmount = 2,
  overlayOpacity = 0.6,
  imageUrl: overrideImageUrl,
  showAuthor = true,
  showTopic = true,
  showTags = true,
  showReadingTime = true,
  showDate = true,
  showDescription = true,
  imageClassName = '',
  contentClassName = '',
  priority = true
}: BlogHeroProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  })
  const prefersReducedMotion = useReducedMotion()

  // Create static motion values once so we can switch to them without re-subscribing to scroll events
  // when the user requests reduced motion. This avoids the heavy Framer listeners entirely in that mode.
  const staticY = useMotionValue("0%")
  const staticOpacity = useMotionValue(1)
  const staticBlur = useMotionValue(0)
  const staticOverlayOpacity = useMotionValue(overlayOpacity)

  useEffect(() => {
    staticOverlayOpacity.set(overlayOpacity)
  }, [overlayOpacity, staticOverlayOpacity])

  // Void ops for future TODOs
  void showAuthor
  void showTags
  void showReadingTime
  void showDate
  void contentClassName

  // Memoize expensive calculations
  const { finalImageUrl, containerHeight } = useMemo(() => {
    // Use override imageUrl if provided, otherwise fall back to hero image
    const imageUrl = overrideImageUrl ?? post.images.hero.url ?? DEFAULT_IMAGE
    
    // Container height based on size prop using modern pattern
    const heightMap = {
      small: 'h-[35vh]',
      medium: 'h-[50vh]',
      large: 'h-[65vh]'
    } as const
    
    return {
      finalImageUrl: imageUrl,
      containerHeight: heightMap[size]
    }
  }, [overrideImageUrl, post.images.hero.url, size])

  // Parallax and visual effects - start at 0 and increase with scroll
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"])
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0])
  const blurStrength = useTransform(scrollYProgress, [0, 1], [0, blurAmount])
  const overlayOpacityValue = useTransform(scrollYProgress, [0, 1], [0, overlayOpacity])

  const effectiveY = prefersReducedMotion ? staticY : y
  const effectiveOpacity = prefersReducedMotion ? staticOpacity : opacity
  const effectiveBlur = prefersReducedMotion ? staticBlur : blurStrength
  const effectiveOverlayOpacity = prefersReducedMotion ? staticOverlayOpacity : overlayOpacityValue
  
  // Using useMotionTemplate avoids calling .get() every frame, which previously forced
  // synchronous reads and contributed to animation jank on long posts.
  const blurFilter = useMotionTemplate`blur(${effectiveBlur}px)`

  return (
    <div 
      ref={ref} 
      className={cn('relative overflow-hidden', containerHeight)}
      role="banner"
      aria-label="Hero section"
    >
      {/* Background Image with Parallax */}
      <motion.div
        style={{ y: effectiveY, opacity: effectiveOpacity }}
        className="absolute inset-0"
      >
        {/* Blur Container */}
        <motion.div
          style={{
            filter: blurFilter,
            WebkitFilter: blurFilter
          }}
          className="absolute inset-0 scale-110" // Scale up to prevent blur edges
        >
          <Image
            src={withDevCacheBust(finalImageUrl)}
            alt={post.images.hero.alt ?? post.title}
            className={cn('object-cover', imageClassName)}
            fill
            priority={priority}
            sizes="100vw"
            quality={85}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
            crossOrigin="anonymous"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
          />
        </motion.div>

        {/* Dynamic Gradient Overlay */}
        <motion.div
          style={{ opacity: effectiveOverlayOpacity }}
          className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"
          aria-hidden="true"
        />
      </motion.div>

      {/* Content Section */}
      {showDescription && (
        <div className="relative h-full">
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="max-w-3xl text-center text-white px-4 backdrop-blur-sm bg-black/5 rounded-2xl p-8"
            >
              {showTopic && post.topic && (
                <Link
                  href={post.topic.href}
                  className="inline-block text-sm font-medium text-white/90 hover:text-white transition-colors"
                >
                  {post.topic.title}
                </Link>
              )}
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-6xl text-white/95 drop-shadow-md">
                {post.title}
              </h1>
              <p className="mt-6 text-xl text-gray-100/90 drop-shadow-sm">
                {post.description}
              </p>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="mt-8"
              >
                <Link
                  href={post.href}
                  className="inline-block rounded-lg bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/20 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {/* sr-only post title: a bare "Read More" is ambiguous to
                      screen-reader users navigating by links (WCAG 2.4.4). */}
                  Read More<span className="sr-only">: {post.title}</span>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  )
}